import styled from 'styled-components';
import React, {useEffect} from "react";
import {useLocation} from "react-router-dom";
import {useMakerUserDetail} from "../../../HomeTown/hooks/Admin/CommercialRegist/GetMakerUserDetail";
import {MEDIA_URL} from "../../../app/constants/APIKEYS";
import {DeskTopCalVW, media} from "../../../utils/CalVW";

/* ====== Color Tokens ====== */
const COLORS = {
  primary: '#FF2828',   // 로고 포인트 컬러
  ink: '#0f172a',       // 본문 텍스트
  grayBg: '#F8F9FA',    // 카드 배경
  grayLine: '#E5E7EB',  // 라인/보더
  shadow: 'rgba(0,0,0,0.06)'
};

/* ====== Layout ====== */
const Container = styled.div`
  width: 100%;
  min-height: 100%;
  padding: 88px 320px 120px;

  @media ${media.desktop} {
    padding: ${DeskTopCalVW(88)} ${DeskTopCalVW(320)} ${DeskTopCalVW(120)};
  }

  background: #ffffff;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;

  @media ${media.desktop} {
    gap: ${DeskTopCalVW(40)};
  }

  max-width: 1280px;
  margin: 0 auto;
`;

/* ====== Top: Image strip ====== */
const ImagesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  width: 100%;
  height: 280px;

  @media ${media.desktop} {
    gap: ${DeskTopCalVW(24)};
    height: ${DeskTopCalVW(280)};
  }
`;

const ImageCard = styled.div`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  background: ${COLORS.grayBg};
  box-shadow:
      0 6px 16px ${COLORS.shadow},
      inset 0 0 0 1px #fff;

  img{
    width: 100%;
    height: 100%;
    object-fit: contain;
    display:block;
    transition: transform .4s ease;
  }

  &:hover img{
    transform: scale(1.03);
  }
`;

/* ====== Middle: Info pills row ====== */
const InfoRow = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;

  @media ${media.desktop} {
    gap: ${DeskTopCalVW(14)};
  }
`;

const InfoPill = styled.div`
  border-radius: 18px;
  padding: 18px 20px;
  background: ${COLORS.grayBg};
  border: 1px solid ${COLORS.grayLine};
  box-shadow: 0 4px 10px ${COLORS.shadow};
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 86px;

  @media ${media.desktop} {
    border-radius: ${DeskTopCalVW(18)};
    padding: ${DeskTopCalVW(18)} ${DeskTopCalVW(20)};
    min-height: ${DeskTopCalVW(86)};
  }
`;

const PillLabel = styled.span`
  font-size: .875rem;
  font-weight: 700;
  letter-spacing: .02em;
  color: ${COLORS.primary};
  opacity: .9;

  @media ${media.desktop} {
    font-size: ${DeskTopCalVW(14)};
  }
`;

const PillValue = styled.span`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${COLORS.ink};
  line-height: 1.4;
  word-break: break-word;

  @media ${media.desktop} {
    font-size: ${DeskTopCalVW(18)};
  }
`;

/* ====== Bottom: Big description card ====== */
const DescCard = styled.section`
  width: 100%;
  background: ${COLORS.grayBg};
  border: 1px solid ${COLORS.grayLine};
  box-shadow: 0 10px 30px ${COLORS.shadow};
  padding: 36px 40px;

  @media ${media.desktop} {
    padding: ${DeskTopCalVW(36)} ${DeskTopCalVW(40)};
  }
`;

const DescTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 800;
  color: ${COLORS.primary};
  margin: 0 0 16px;

  @media ${media.desktop} {
    font-size: ${DeskTopCalVW(28)};
    margin-bottom: ${DeskTopCalVW(16)};
  }
`;

const DescBody = styled.p`
  font-size: 1.125rem;
  line-height: 1.9;
  color: ${COLORS.ink};
  white-space: pre-line;

  @media ${media.desktop} {
    font-size: ${DeskTopCalVW(18)};
    line-height: ${DeskTopCalVW(34)}/${DeskTopCalVW(18)};
  }
`;

/* ====== Helpers ====== */
const fallback = (v) => (v && String(v).trim().length ? v : '-');

function ThirdContent() {
  const location = useLocation();
  const { id: postId } = location?.state || {};
  const { makerUserDetail } = useMakerUserDetail(postId);

  useEffect(() => {
    console.log('makerUserDetail', makerUserDetail);
  }, [makerUserDetail]);

  const company =
      makerUserDetail?.companyName ||
      makerUserDetail?.company ||
      makerUserDetail?.name ||
      makerUserDetail?.makerName ||
      '-';

  const address = fallback(makerUserDetail?.address);
  const email   = fallback(makerUserDetail?.email);
  const phone   = fallback(makerUserDetail?.phone);
  const memo    = fallback(makerUserDetail?.memo);

  console.log('//makerUserDetail',makerUserDetail)

  return (
      <Container>
        <Content>

          {/* Top: 3 images */}
          {makerUserDetail?.fileList?.length ? (
              <ImagesContainer>
                {makerUserDetail.fileList.slice(0,3).map((img, idx) => (
                    <ImageCard key={`${img?.url || idx}`}>
                      <img src={MEDIA_URL + img?.url} alt={`gallery-${idx+1}`} />
                    </ImageCard>
                ))}
              </ImagesContainer>
          ) : (
              <ImagesContainer>
                {[0,1,2].map((i)=>(
                    <ImageCard key={i} />
                ))}
              </ImagesContainer>
          )}

          {/* Middle: info pills */}
          <InfoRow>
            <InfoPill>
              <PillLabel>업체명</PillLabel>
              <PillValue>{company}</PillValue>
            </InfoPill>
            <InfoPill>
              <PillLabel>주소</PillLabel>
              <PillValue>{address}</PillValue>
            </InfoPill>
            <InfoPill>
              <PillLabel>이메일 주소</PillLabel>
              <PillValue>{email}</PillValue>
            </InfoPill>
            <InfoPill>
              <PillLabel>전화번호</PillLabel>
              <PillValue>{phone}</PillValue>
            </InfoPill>
          </InfoRow>

          {/* Bottom: big description */}
          <DescCard>
            <DescTitle>서비스 설명</DescTitle>
            <DescBody>{memo}</DescBody>
          </DescCard>
        </Content>
      </Container>
  );
}

export default ThirdContent;
